import { FC, useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { MultiSelectDataDropdown, MultiSelectDropdown, SubmitButton, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons, useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { GroupListResult, PlanAccountListResult, PlanListResult } from "capitalrx-types";

import { PricingLevelFilterFormType } from "..";
import { getApiBaseUrl } from "../../../api";
import { Network, PlanBenefit } from "../../../api/interfaces";
import { agreementChannel, lineOfBusiness, providerAgreementType } from "../../../constants";
import { useFailureHandler } from "../../../hooks";
import { generateMultiSelectDropdownOptions } from "../../../utils";

interface PricingLevelFilterFormProps {
  submitForm: (data: PricingLevelFilterFormType) => Promise<void>;
}

export const PricingLevelFilterForm: FC<PricingLevelFilterFormProps> = (props) => {
  const { submitForm } = props;

  const failureHandler = useFailureHandler();
  const { columns321, isMobile } = useResponsive({
    isMobile: { desktop: false, mobile: true },
    columns321: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
  });
  const { reset, getValues, handleSubmit } = useFormContext();
  const clientId = useWatch({ name: "client_ids" });
  const external_network_ids = useWatch({ name: "external_network_ids" });

  const clientIdDataSource = useRemoteTableDataSource<PlanListResult>(
    `${getApiBaseUrl()}/plan/list`,
    (row: PlanListResult) => row?.plan.client_id,
    {
      idFilterName: "client_ids",
    },
  );

  const networkDataSourceDropdownData = useRemoteTableDataSource<Network.NetworkAPIResult>(
    `${getApiBaseUrl()}/network/list`,
    (row) => row.network.external_network_id,
    {
      pageSize: 15,
      idFilterName: "external_network_ids",
      onError: failureHandler,
    },
  );

  const csidDataSourceDropdownData = useRemoteTableDataSource<PlanBenefit.PlanBenefitListResult>(
    `${getApiBaseUrl()}/plan_benefit/list`,
    (row) => row?.plan_benefit?.coverage_strategy_id,
    {
      pageSize: 15,
      idFilterName: "coverage_strategy_ids",
      onError: failureHandler,
      filter: {
        only_fields: ["coverage_strategy_id", "name"],
        group_by_coverage_strategy_id: true,
      },
    },
  );

  const listGroupDataSource = useRemoteTableDataSource<GroupListResult>(`${getApiBaseUrl()}/group/list`, (row) => row?.group?.external_group_id, {
    pageSize: 15,
    idFilterName: "group_ids",
  });

  const listPlanAccountDataSource = useRemoteTableDataSource<PlanAccountListResult>(
    `${getApiBaseUrl()}/plan_account/list`,
    (row) => row?.plan_account?.external_account_id,
    {
      pageSize: 15,
      idFilterName: "account_ids",
    },
  );

  const csidlabelProvider = useCallback((row: PlanBenefit.PlanBenefitListResult) => {
    return `${row?.plan_benefit?.name} | ${row?.plan_benefit?.coverage_strategy_id}`;
  }, []);

  const networklabelProvider = useCallback((row: Network.NetworkAPIResult) => {
    return `${row.network.external_network_id} - ${row.network.name}`;
  }, []);

  const clientIdLabelProvider = useCallback((row: PlanListResult) => {
    return `${row?.plan?.client_id}`;
  }, []);

  const externalGroupIDLabelProvider = useCallback((row: GroupListResult) => {
    return `${row?.group?.external_group_id}`;
  }, []);

  const externalAccountIDLabelProvider = useCallback((row: PlanAccountListResult) => {
    return `${row?.plan_account?.external_account_id}`;
  }, []);

  const resetForm = useCallback(() => {
    reset(
      Object.keys(getValues()).reduce<{ [k: string]: null }>((prev, next) => {
        prev[next] = null;
        return prev;
      }, {}),
    );
    handleSubmit(submitForm)();
  }, [getValues, handleSubmit, reset, submitForm]);

  const filters = useMemo(
    () => (
      <Grid>
        <Grid.Spread columns={columns321}>
          <MultiSelectDataDropdown.Field<PlanListResult>
            name="client_ids"
            label="Client ID(s)"
            testId="test__client_id"
            dataSource={clientIdDataSource}
            searchField="client_id"
            optionLabelProvider={clientIdLabelProvider}
            placeholder="Select"
          />
          <MultiSelectDataDropdown.Field<GroupListResult>
            dataSource={listGroupDataSource}
            name="external_group_ids"
            label="External Group ID"
            optionLabelProvider={externalGroupIDLabelProvider}
            placeholder="Select"
            searchField="external_group_id"
            testId="test__external_group_id"
          />
          <MultiSelectDataDropdown.Field<PlanAccountListResult>
            dataSource={listPlanAccountDataSource}
            name="external_account_ids"
            label="External Account ID"
            optionLabelProvider={externalAccountIDLabelProvider}
            placeholder="Select"
            searchField="external_account_id"
            testId="test__external_account_id"
          />
          <MultiSelectDropdown.Field label="Line(s) of Business" name="lines_of_business" placeholder="Select">
            {generateMultiSelectDropdownOptions(lineOfBusiness)}
          </MultiSelectDropdown.Field>
          <Textbox.Field name="notes" label="Notes" testId="test__notes" />
          <MultiSelectDataDropdown.Field
            dataSource={csidDataSourceDropdownData}
            optionLabelProvider={csidlabelProvider}
            name="coverage_strategy_ids"
            searchField="full_text_search"
            testId="test__coverage-strategy-ids"
            label="Coverage Strategy ID(s)"
            placeholder="Select"
          />
          <MultiSelectDataDropdown.Field<Network.NetworkAPIResult>
            name="external_network_ids"
            label="Network(s)"
            testId="test__external_network_ids"
            dataSource={networkDataSourceDropdownData}
            searchField="external_network_id"
            optionLabelProvider={networklabelProvider}
            placeholder="Select"
          />
          <MultiSelectDropdown.Field
            label="Provider Agreement Type(s)"
            testId="test__provider_agreement_types"
            name="agreement_types"
            placeholder="Select"
          >
            {generateMultiSelectDropdownOptions(providerAgreementType)}
          </MultiSelectDropdown.Field>
          <MultiSelectDropdown.Field
            label="Provider Agreement Channel(s)"
            testId="test__agreement_channels"
            name="agreement_channels"
            placeholder="Select"
          >
            {generateMultiSelectDropdownOptions(agreementChannel)}
          </MultiSelectDropdown.Field>
        </Grid.Spread>
      </Grid>
    ),
    [clientIdLabelProvider, clientIdDataSource, listGroupDataSource, listPlanAccountDataSource, clientId, external_network_ids],
  );

  return (
    <Stack>
      {filters}
      {!isMobile && (
        <Stack spacing="mini" alignment="left">
          <Shelf spacing="small">
            <SubmitButton appearance={Buttons.Functional.Primary.Large}>Filter</SubmitButton>
            <Button appearance={Buttons.Functional.Secondary.Large} testId="reset" onClick={resetForm}>
              Reset
            </Button>
          </Shelf>
        </Stack>
      )}
      {isMobile && (
        <Stack spacing="small">
          <SubmitButton appearance={Buttons.Functional.Primary.Large}>Filter</SubmitButton>
          <Stack.Spacer />
          <Button appearance={Buttons.Functional.Secondary.Large} testId="reset" onClick={resetForm}>
            Reset
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
